/**
 * What a broker sees in a spotlight, and the caps every writer is held to
 * (spec cova-feed/2026-10-09-001 §5.1).
 *
 * The content is DERIVED from the post (`derive-spotlight.ts`); what is stored
 * on the post is only the author's overrides, a partial of this shape. The
 * caps live here so the composer's counters and the admin service's
 * validation read one number.
 *
 * Frontend-safe.
 */
import { Schema as S } from "effect"
import { CtaSpec } from "./spotlight-action"

export const HEADLINE_MAX = 80
export const SUMMARY_MAX = 220
export const CTA_LABEL_MAX = 28
export const HIGHLIGHTS_MAX = 3
export const HIGHLIGHT_TITLE_MAX = 60
export const HIGHLIGHT_DETAIL_MAX = 96
export const AGENT_PROMPT_MAX = 500
/** A hero image's alt text and a cover's label. Not in the spec's cap list; bounded so a JSON writer cannot store a page of text. */
export const HERO_TEXT_MAX = 160

export const SpotlightHero = S.Union(
  /** The post's feature image, or a spotlight-only upload in the FeedPostImage context. */
  S.Struct({ kind: S.Literal("image"), fileUuid: S.String, alt: S.String }),
  /** The post's video, with its poster frame when it has one. */
  S.Struct({ kind: S.Literal("video"), fileUuid: S.String, posterFileUuid: S.NullOr(S.String) }),
  /** No picture: a cover drawn in the category's colour. */
  S.Struct({ kind: S.Literal("cover"), label: S.String, color: S.String }),
)
export type SpotlightHero = typeof SpotlightHero.Type

export const Highlight = S.Struct({ title: S.String, detail: S.String })
export type Highlight = typeof Highlight.Type

export const SpotlightContent = S.Struct({
  hero: SpotlightHero,
  headline: S.String,
  summary: S.String,
  highlights: S.Array(Highlight),
  cta: CtaSpec,
})
export type SpotlightContent = typeof SpotlightContent.Type

/** The author's overrides, keyed by field. An absent key follows the post. */
export const SpotlightOverrides = S.partialWith(SpotlightContent, { exact: true })
export type SpotlightOverrides = typeof SpotlightOverrides.Type

export type SpotlightFieldKey = keyof SpotlightContent

/** In the order the popup reads, which is the order the composer lists them. */
export const SPOTLIGHT_FIELD_KEYS: ReadonlyArray<SpotlightFieldKey> = ["hero", "headline", "summary", "highlights", "cta"]

export const SPOTLIGHT_FIELD_LABELS: Readonly<Record<SpotlightFieldKey, string>> = {
  hero: "Picture",
  headline: "Headline",
  summary: "Summary",
  highlights: "What's new",
  cta: "Button",
}
